import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const emptyEntry = () => ({
  entry_type: 'income',
  status: 'paid',
  amount: '',
  category: '',
  description: '',
  occurred_on: new Date().toISOString().slice(0, 10),
  notes: ''
})

const money = (value) => new Intl.NumberFormat('el-GR', {
  style: 'currency',
  currency: 'EUR'
}).format(Number(value || 0))

export default function ProjectFinanceManager() {
  const location = useLocation()
  const [projects, setProjects] = useState([])
  const [selectedProjectId, setSelectedProjectId] = useState(location.state?.projectId || '')
  const [entries, setEntries] = useState([])
  const [form, setForm] = useState(emptyEntry())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    loadProjects()
  }, [])

  useEffect(() => {
    if (selectedProjectId) loadEntries(selectedProjectId)
    else setEntries([])
  }, [selectedProjectId])

  async function loadProjects() {
    try {
      setLoading(true)
      const { data, error: rpcError } = await supabase.rpc('codevaro_internal_projects_admin_list')
      if (rpcError) throw rpcError
      setProjects(data || [])
      if (!selectedProjectId && data?.length) setSelectedProjectId(data[0].id)
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα φόρτωσης projects: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  async function loadEntries(projectId) {
    try {
      setError('')
      const { data, error: entriesError } = await supabase
        .from('codevaro_project_financial_entries')
        .select('*')
        .eq('project_id', projectId)
        .order('occurred_on', { ascending: false })
        .order('created_at', { ascending: false })
      if (entriesError) throw entriesError
      setEntries(data || [])
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα φόρτωσης οικονομικών: ${err.message}`)
    }
  }

  const totals = useMemo(() => {
    const paidIncome = entries.filter((x) => x.entry_type === 'income' && x.status === 'paid').reduce((s, x) => s + Number(x.amount || 0), 0)
    const paidExpense = entries.filter((x) => x.entry_type === 'expense' && x.status === 'paid').reduce((s, x) => s + Number(x.amount || 0), 0)
    const plannedIncome = entries.filter((x) => x.entry_type === 'income' && x.status === 'planned').reduce((s, x) => s + Number(x.amount || 0), 0)
    const plannedExpense = entries.filter((x) => x.entry_type === 'expense' && x.status === 'planned').reduce((s, x) => s + Number(x.amount || 0), 0)
    return { paidIncome, paidExpense, plannedIncome, plannedExpense, profit: paidIncome - paidExpense }
  }, [entries])

  async function addEntry(e) {
    e.preventDefault()
    if (!selectedProjectId) return
    try {
      setSaving(true)
      setError('')
      setSuccess('')
      const amount = Number(form.amount)
      if (!Number.isFinite(amount) || amount <= 0) throw new Error('Το ποσό πρέπει να είναι μεγαλύτερο από 0')
      const { error: insertError } = await supabase.from('codevaro_project_financial_entries').insert({
        project_id: selectedProjectId,
        entry_type: form.entry_type,
        status: form.status,
        amount,
        category: form.category.trim() || null,
        description: form.description.trim(),
        occurred_on: form.occurred_on,
        notes: form.notes.trim() || null
      })
      if (insertError) throw insertError
      setForm(emptyEntry())
      await loadEntries(selectedProjectId)
      setSuccess('Η οικονομική εγγραφή προστέθηκε.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  async function deleteEntry(id) {
    if (!window.confirm('Διαγραφή αυτής της εγγραφής;')) return
    try {
      const { error: deleteError } = await supabase.from('codevaro_project_financial_entries').delete().eq('id', id)
      if (deleteError) throw deleteError
      await loadEntries(selectedProjectId)
    } catch (err) {
      setError(`Σφάλμα διαγραφής: ${err.message}`)
    }
  }

  const selectedProject = projects.find((p) => p.id === selectedProjectId)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Project Finance</h1>
        <p className="mt-1 text-sm text-gray-600">Έσοδα, έξοδα, προγραμματισμένα ποσά και καθαρό αποτέλεσμα ανά project.</p>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700">{success}</div>}

      <div className="rounded-xl bg-white p-5 shadow">
        <label className="mb-2 block text-sm font-medium text-gray-700">Project</label>
        <select value={selectedProjectId} onChange={(e) => setSelectedProjectId(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2">
          <option value="">Επιλέξτε project</option>
          {projects.map((project) => <option key={project.id} value={project.id}>{project.title} — {project.client_name || project.lead_name || 'χωρίς πελάτη'}</option>)}
        </select>
      </div>

      {selectedProjectId && (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <div className="rounded-xl bg-white p-4 shadow"><div className="text-xs text-gray-500">Πληρωμένα Έσοδα</div><div className="mt-1 text-xl font-bold text-green-700">{money(totals.paidIncome)}</div></div>
            <div className="rounded-xl bg-white p-4 shadow"><div className="text-xs text-gray-500">Πληρωμένα Έξοδα</div><div className="mt-1 text-xl font-bold text-red-700">{money(totals.paidExpense)}</div></div>
            <div className="rounded-xl bg-white p-4 shadow"><div className="text-xs text-gray-500">Καθαρό</div><div className={`mt-1 text-xl font-bold ${totals.profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>{money(totals.profit)}</div></div>
            <div className="rounded-xl bg-white p-4 shadow"><div className="text-xs text-gray-500">Προγρ. Έσοδα</div><div className="mt-1 text-xl font-bold text-blue-700">{money(totals.plannedIncome)}</div></div>
            <div className="rounded-xl bg-white p-4 shadow"><div className="text-xs text-gray-500">Προγρ. Έξοδα</div><div className="mt-1 text-xl font-bold text-amber-700">{money(totals.plannedExpense)}</div></div>
          </div>

          <form onSubmit={addEntry} className="rounded-xl bg-white p-5 shadow space-y-4">
            <div className="flex items-center gap-2"><SafeIcon icon={FiIcons.FiDollarSign} className="h-5 w-5" /><h2 className="font-semibold text-gray-900">Νέα εγγραφή — {selectedProject?.title}</h2></div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <select value={form.entry_type} onChange={(e) => setForm((p) => ({ ...p, entry_type: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2"><option value="income">Έσοδο</option><option value="expense">Έξοδο</option></select>
              <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2"><option value="paid">Πληρωμένο</option><option value="planned">Προγραμματισμένο</option></select>
              <input type="number" min="0.01" step="0.01" required placeholder="Ποσό €" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
              <input type="text" placeholder="Κατηγορία π.χ. Hosting" value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
              <input type="date" required value={form.occurred_on} onChange={(e) => setForm((p) => ({ ...p, occurred_on: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
              <input type="text" required placeholder="Περιγραφή" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2" />
            </div>
            <textarea rows={2} placeholder="Σημειώσεις" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
            <button type="submit" disabled={saving} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50">{saving ? 'Αποθήκευση...' : 'Προσθήκη εγγραφής'}</button>
          </form>

          <div className="rounded-xl bg-white shadow overflow-hidden">
            {loading ? <div className="p-8 text-center">Φόρτωση...</div> : entries.length === 0 ? <div className="p-8 text-center text-gray-500">Δεν υπάρχουν οικονομικές εγγραφές.</div> : (
              <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200"><thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Ημερομηνία</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Τύπος</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Περιγραφή</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Κατηγορία</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Status</th><th className="px-4 py-3 text-right text-xs uppercase text-gray-500">Ποσό</th><th className="px-4 py-3"></th></tr></thead><tbody className="divide-y divide-gray-100">{entries.map((entry) => <tr key={entry.id}><td className="px-4 py-3 text-sm">{entry.occurred_on}</td><td className="px-4 py-3 text-sm">{entry.entry_type === 'income' ? 'Έσοδο' : 'Έξοδο'}</td><td className="px-4 py-3 text-sm">{entry.description}</td><td className="px-4 py-3 text-sm">{entry.category || '—'}</td><td className="px-4 py-3 text-sm">{entry.status === 'paid' ? 'Πληρωμένο' : 'Προγραμματισμένο'}</td><td className={`px-4 py-3 text-right font-semibold ${entry.entry_type === 'income' ? 'text-green-700' : 'text-red-700'}`}>{entry.entry_type === 'income' ? '+' : '-'}{money(entry.amount)}</td><td className="px-4 py-3 text-right"><button onClick={() => deleteEntry(entry.id)} className="text-red-600 hover:text-red-800">Διαγραφή</button></td></tr>)}</tbody></table></div>
            )}
          </div>
        </>
      )}
    </div>
  )
}